import React, { useEffect, useState } from 'react';
import { getMostUsedScenarios, getLeastUsedScenarios } from "../../requests.js";
import { useDashboardFilters } from './context/DashboardContext.js';
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer.js";
import ResponseLoader from '../common/ResponseLoader.js';
import styles from "../../_css/ReportingAndSterring.module.css";

const initialMostUsed = [
    { name: 'Objection Handling', usage: 142, avgScore: '83%', completion: '97%' },
    { name: 'Conflict Resolution', usage: 128, avgScore: '79%', completion: '91%' },
    { name: 'Sales Pitch Practice', usage: 112, avgScore: '88%', completion: '94%' },
];

const initialLeastUsed = [
    { name: 'Crisis Negotiation', usage: 9, avgScore: '72%', completion: '58%' },
    { name: 'Team Motivation Drill', usage: 11, avgScore: '62%', completion: '62%' },
    { name: 'Sales Pitch Practice', usage: 20, avgScore: '65%', completion: '48%' },
];

const ScenarioUsage = () => {
    const [mostUsed, setMostUsed] = useState([]);
    const [leastUsed, setLeastUsed] = useState([]);
    const [loading, setLoading] = useState(false);
    const { filters } = useDashboardFilters();
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            dispatch(startLoader());
            try {
                const startDate = filters.dateRange.from;
                const endDate = filters.dateRange.to;
                const companyName = filters.company;

                const [mostRes, leastRes] = await Promise.all([
                    getMostUsedScenarios({ startDate, endDate, companyName, limit: 3 }),
                    getLeastUsedScenarios({ startDate, endDate, companyName, limit: 3 }),
                ]);
                // console.log("Most used scenario: ", mostRes);

                // Check if API returned actual data
                const hasMostUsedData = mostRes?.data?.success && mostRes?.data?.data && mostRes.data.data.length > 0;
                const hasLeastUsedData = leastRes?.data?.success && leastRes?.data?.data && leastRes.data.data.length > 0;

                if (hasMostUsedData) {
                    const mergedMost = mostRes.data.data.map((apiItem, idx) => {
                        const initItem = initialMostUsed[idx] || {};
                        return {
                            name: apiItem?.scenario_name || initItem.name || `Scenario ${idx + 1}`,
                            usage: apiItem?.usage_count ?? initItem.usage ?? 0,
                            avgScore: apiItem?.avgScore || initItem.avgScore || '0%',
                            completion: apiItem?.completion_rate || initItem.completion || '0%',
                        };
                    });
                    setMostUsed(mergedMost);
                } else {
                    setMostUsed([]); // Set empty array if no data
                }

                if (hasLeastUsedData) {
                    const mergedLeast = leastRes.data.data.map((apiItem, idx) => {
                        const initItem = initialLeastUsed[idx] || {};
                        return {
                            name: apiItem?.scenario_name || initItem.name || `Scenario ${idx + 1}`,
                            usage: apiItem?.usage_count ?? initItem.usage ?? 0,
                            avgScore: apiItem?.avgScore || initItem.avgScore || '0%',
                            completion: apiItem?.completion_rate || initItem.completion || '0%',
                        };
                    });
                    setLeastUsed(mergedLeast);
                } else {
                    setLeastUsed([]); // Set empty array if no data
                }

            } catch (err) {
                console.error("Error fetching scenarios:", err);
                setMostUsed([]);
                setLeastUsed([]);
            } finally {
                setLoading(false);
                dispatch(stopLoader());
            }
        };

        fetchData();
    }, [filters, dispatch]);

    const renderNoData = (title) => (
        <div className={styles.noDataContainer}>
            <p className={styles.noDataTitle}>
                No data to show
            </p>
            <p className={styles.noDataSubtitle}>
                {title} data not available
            </p>
        </div>
    );

    const renderTable = (data, type) => {
        // If no data and not loading, show no data message
        if (!loading && data.length === 0) {
            return renderNoData(type === 'most' ? 'Most used scenarios' : 'Least used scenarios');
        }

        return (
            <table className={`table table-borderless mb-0 ${styles.scenarioTable}`}>
                <thead className={styles.tableHead}>
                    <tr>
                        <th className={styles.scenarioNameHeader}>
                            Scenario Name
                        </th>
                        <th className={styles.usageHeader}>Usage Count</th>
                        <th className={styles.scoreHeader}>Avg. Score</th>
                        <th className={styles.completionHeader}>Completion Rate</th>
                    </tr>
                </thead>
                <tbody className={styles.tableBody}>
                    {data.map((item, idx) => {
                        const isLowScore = parseFloat(item.avgScore) < 70;
                        return (
                            <tr key={idx}>
                                <td className={styles.scenarioNameCell} title={item.name}>
                                    {item.name}
                                </td>
                                <td className={styles.usageCell}>{item.usage}</td>
                                <td className={styles.scoreCell}>
                                    <span className={`${styles.scoreBadge} ${isLowScore ? styles.lowScore : styles.highScore}`}>
                                        {item.avgScore}
                                    </span>
                                </td>
                                <td className={styles.completionCell}>{item.completion}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    return (
        <div className="row g-4">
            <div className="col-md-6">
                <div className={`p-4 rounded ${styles.scenarioCard}`}>
                    <h5 className={styles.mostUsedTitle}>
                        Most Used Scenarios
                    </h5>

                    {loading ? (
                        <div className={styles.loaderContainer}>
                            <ResponseLoader />
                        </div>
                    ) : (
                        renderTable(mostUsed, 'most')
                    )}
                </div>
            </div>

            <div className="col-md-6">
                <div className={`p-4 rounded ${styles.scenarioCard}`}>
                    <h5 className={styles.leastUsedTitle}>
                        Least Used Scenarios
                    </h5>

                    {loading ? (
                        <div className={styles.loaderContainer}>
                            <ResponseLoader />
                        </div>
                    ) : (
                        renderTable(leastUsed, 'least')
                    )}
                </div>
            </div>
        </div>
    );
};

export default ScenarioUsage;